import { useState, type ElementType } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertTriangle,
  ArrowRight,
  BarChart3,
  Building2,
  Calendar,
  CheckCircle2,
  FileText,
  Shield,
  Users,
  X,
} from 'lucide-react';

interface MemoFiscalBZ2026ModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type TabId = 'resume' | 'vendeurs' | 'structure' | 'acquisico' | 'fiscal' | 'calendrier' | 'sources';

const tabs: { id: TabId; label: string; icon: ElementType }[] = [
  { id: 'resume', label: 'Résumé CA', icon: BarChart3 },
  { id: 'vendeurs', label: 'Vendeurs', icon: Building2 },
  { id: 'structure', label: 'Actionnariat', icon: Users },
  { id: 'acquisico', label: 'AcquisiCo', icon: Shield },
  { id: 'fiscal', label: 'Fiscalité', icon: Shield },
  { id: 'calendrier', label: 'Dates & risques', icon: Calendar },
  { id: 'sources', label: 'Sources', icon: FileText },
];

const transactionSteps = [
  {
    title: '1. Nouvelle AcquisiCo',
    text: '9557-1782 Québec inc. est le véhicule commun pour l’entrée des nouveaux actionnaires.',
    tag: 'Étape 5.1',
  },
  {
    title: '2. Vente directe BZ',
    text: 'Dany/GestionDB vend 0,01 % à AcquisiCo. Le groupe Zicat vend 12,49 % au total: 10,49 % à AcquisiCo et 2,00 % directement à Richard.',
    tag: 'BZ',
  },
  {
    title: '3. Vente Octet',
    text: 'Gestion B&D vend sa participation dans Gestion Octet à AcquisiCo, soit 16,67 % d’Octet, équivalent économique d’environ 3,50 % de BZ.',
    tag: 'Octet',
  },
  {
    title: '4. Nettoyage fiscal',
    text: 'Échanges d’actions, DGC/ECGC, IMR possible et déclarations 2026/2027 à gérer.',
    tag: 'Fiscal',
  },
];

const ownership = [
  { name: 'Dany / GestionDB', before: 33.49, after: 33.48, note: 'Dany/GestionDB vend 0,01 % à AcquisiCo. Le 2,00 % de Jérôme est déjà acheté par Dany en avril et demeure chez Dany; il ne fait pas partie du bloc vendu à AcquisiCo/Richard.' },
  { name: 'AcquisiCo', before: 0, after: 14.0, note: '10,50 % direct BZ + environ 3,50 % indirect via Octet.' },
  { name: 'François Zicat / GestionFZ + FiducieFZ', before: 17.49, after: 5.0, note: 'Vente totale supportée par le mémo: 12,49 %, soit 10,49 % à AcquisiCo et 2,00 % à Richard.' },
  { name: 'Gestion Octet', before: 21.0, after: 21.0, note: 'La composition interne change par l’entrée d’AcquisiCo.' },
  { name: 'Richard Délisle', before: 3.5, after: 5.5, note: 'Ajout direct estimé de 2,00 %.' },
];

const sellerBridge = [
  {
    seller: 'Groupe Zicat',
    sold: '12,49 %',
    buyer: '10,49 % AcquisiCo + 2,00 % Richard',
    source: 'Étape 5.3',
    note: 'Le mémo parle de GestionFZ et FiducieFZ ensemble. 17,49 % initial vers 5,00 % final = 12,49 % vendu.',
  },
  {
    seller: 'Gestion Bouchard et Drouin',
    sold: '~3,50 % économique BZ',
    buyer: 'AcquisiCo via Gestion Octet',
    source: 'Étape 5.4',
    note: 'B&D vend 10 actions A de Gestion Octet, soit 16,67 % d’Octet. Comme Octet détient 21,00 % de BZ, l’équivalent économique est environ 3,50 % de BZ.',
  },
  {
    seller: 'Dany / GestionDB',
    sold: '0,01 %',
    buyer: 'AcquisiCo',
    source: 'Étape 5.2',
    note: 'Vente technique d’une action PA et d’une action V pour arriver au 10,50 % direct d’AcquisiCo.',
  },
  {
    seller: 'Jérôme Martel',
    sold: '2,00 % déjà complété',
    buyer: 'Dany conserve cette participation',
    source: 'Étape 5.6 / avril 2026',
    note: 'Transaction déjà réalisée et conservée par Dany. À ne pas mélanger avec le bloc AcquisiCo/Richard du 1er janvier.',
  },
];

const acquisiCoShareholders = [
  { name: 'Antoine Giroux', shares: '480 AC', percentage: 21.43 },
  { name: 'Louis-Alexandre Hébert', shares: '480 AD', percentage: 21.43 },
  { name: 'Jean-Pierre Leclerc', shares: '400 AF', percentage: 17.86 },
  { name: 'Jean-Michel Caouette', shares: '320 AE', percentage: 14.29 },
  { name: 'Gestion Nibble inc. / Francis Marquis', shares: '240 AB', percentage: 10.71 },
  { name: 'Mélissa Pelletier', shares: '160 AA', percentage: 7.14 },
  { name: 'Éric Durand', shares: '160 AG', percentage: 7.14 },
];

const fiscalBlocks = [
  {
    title: 'DGC / ECGC',
    value: 'Gain net visé: 0 $',
    text: 'Le mémo prévoit l’utilisation de l’exonération cumulative pour gain en capital lorsque les actions se qualifient comme AAPE.',
  },
  {
    title: 'FiducieFZ',
    value: 'Attributions T3 / RL-16',
    text: 'Une partie du gain imposable est attribuée à François et Marie-Pierre, avec résolutions des fiduciaires et feuillets à produire.',
  },
  {
    title: 'IMR',
    value: 'Risque temporaire',
    text: 'L’IMR peut s’appliquer quand l’ECGC est utilisée. Il dépend des autres revenus et peut être récupérable sur sept ans.',
  },
  {
    title: 'Dany / USA',
    value: 'À coordonner CFLA',
    text: 'Le mémo renvoie le traitement fiscal américain de Dany à CFLA vu la résidence fiscale hors Québec / éléments américains.',
  },
];

const timeline = [
  { date: '1 janv. 2026', title: 'Date économique principale', text: 'Ventes principales de BZ et d’Octet documentées au mémo.' },
  { date: '1 avr. 2026', title: 'Vente Jérôme / Dany', text: 'Transaction déjà réalisée; Dany conserve ce 2,00 %.' },
  { date: '3 juil. 2026', title: 'Version Maxime', text: 'Version sans Yannick envoyée par Maxime et transférée à Dany.' },
  { date: '31 mars 2027', title: 'Feuillets fiducie', text: 'T3 et Relevé 16 à produire pour les attributions FiducieFZ.' },
];

function Metric({ label, value, note }: { label: string; value: string; note: string }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-2 text-2xl font-bold text-slate-950">{value}</p>
      <p className="mt-1 text-sm leading-5 text-slate-600">{note}</p>
    </div>
  );
}

function OwnershipBar({ name, before, after, note }: { name: string; before: number; after: number; note: string }) {
  const max = 34;
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h4 className="font-bold text-slate-950">{name}</h4>
          <p className="text-sm text-slate-600">{note}</p>
        </div>
        <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">{before.toFixed(2)} % → {after.toFixed(2)} %</span>
      </div>
      <div className="space-y-2">
        <div>
          <div className="mb-1 flex justify-between text-xs text-slate-500"><span>Avant</span><span>{before.toFixed(2)} %</span></div>
          <div className="h-2 rounded-full bg-slate-100"><div className="h-2 rounded-full bg-slate-400" style={{ width: `${Math.max(1, (before / max) * 100)}%` }} /></div>
        </div>
        <div>
          <div className="mb-1 flex justify-between text-xs text-slate-500"><span>Après</span><span>{after.toFixed(2)} %</span></div>
          <div className="h-2 rounded-full bg-amber-100"><div className="h-2 rounded-full bg-amber-500" style={{ width: `${Math.max(1, (after / max) * 100)}%` }} /></div>
        </div>
      </div>
    </div>
  );
}

function PercentagePill({ value }: { value: number }) {
  return (
    <div className="flex items-center gap-3">
      <div className="h-2 flex-1 rounded-full bg-slate-100">
        <div className="h-2 rounded-full bg-amber-500" style={{ width: `${value}%` }} />
      </div>
      <span className="w-16 text-right text-sm font-bold text-slate-800">{value.toFixed(2)} %</span>
    </div>
  );
}

export default function MemoFiscalBZ2026Modal({ isOpen, onClose }: MemoFiscalBZ2026ModalProps) {
  const [activeTab, setActiveTab] = useState<TabId>('resume');

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/75 p-4 backdrop-blur-sm"
        >
          <motion.div
            initial={{ opacity: 0, y: 18, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 18, scale: 0.98 }}
            className="flex max-h-[92vh] w-full max-w-7xl flex-col overflow-hidden rounded-xl bg-slate-50 shadow-2xl"
          >
            <div className="border-b border-amber-900/30 bg-gradient-to-r from-slate-950 via-slate-900 to-amber-950 px-6 py-5 text-white">
              <div className="flex items-start justify-between gap-6">
                <div className="flex items-start gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-amber-500/20 ring-1 ring-amber-300/40">
                    <FileText className="h-6 w-6 text-amber-100" />
                  </div>
                  <div>
                    <div className="mb-2 flex flex-wrap gap-2">
                      <span className="rounded-full bg-amber-500 px-3 py-1 text-xs font-bold uppercase tracking-wide text-slate-950">Mémo fiscal</span>
                      <span className="rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-semibold">Version 3 juillet 2026</span>
                      <span className="rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-semibold">CA / source de vérité</span>
                    </div>
                    <h2 className="text-2xl font-bold tracking-tight">Vente d’actions BZ 2026 - lecture visuelle CA</h2>
                    <p className="mt-1 max-w-4xl text-sm leading-6 text-slate-200">
                      Synthèse visuelle du mémo de Maxime Lamoureux pour comprendre rapidement la mécanique de transaction, l’entrée d’AcquisiCo, les impacts fiscaux et les sources originales.
                    </p>
                  </div>
                </div>
                <button type="button" onClick={onClose} className="rounded-lg p-2 text-slate-200 transition hover:bg-white/10 hover:text-white" aria-label="Fermer">
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>

            <div className="border-b border-slate-200 bg-white px-6">
              <div className="flex gap-2 overflow-x-auto py-3">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition ${
                        isActive ? 'bg-slate-950 text-white shadow' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-950'
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                      {tab.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {activeTab === 'resume' && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                    <Metric label="Valeur BZ utilisée" value="14,4 M$" note="Base de calcul du mémo fiscal." />
                    <Metric label="AcquisiCo total" value="14,00 %" note="10,50 % direct BZ + ~3,50 % indirect via Octet." />
                    <Metric label="Richard direct" value="+2,00 %" note="Achat direct de BZ, hors groupe AcquisiCo." />
                    <Metric label="Bloc Jan. 1 vérifié" value="16,00 %" note="12,49 % Zicat + 3,50 % B&D/Octet + 0,01 % DB." />
                  </div>

                  <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                    <h3 className="mb-4 text-lg font-bold text-slate-950">Vue simple de la mécanique</h3>
                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-4">
                      {transactionSteps.map((step, index) => (
                        <div key={step.title} className="relative rounded-lg border border-slate-200 bg-slate-50 p-4">
                          <span className="rounded-full bg-amber-100 px-2 py-1 text-xs font-bold text-amber-800">{step.tag}</span>
                          <h4 className="mt-3 font-bold text-slate-950">{step.title}</h4>
                          <p className="mt-2 text-sm leading-6 text-slate-600">{step.text}</p>
                          {index < transactionSteps.length - 1 && <ArrowRight className="absolute -right-5 top-1/2 hidden h-6 w-6 text-slate-300 lg:block" />}
                        </div>
                      ))}
                    </div>
                  </section>

                  <div className="rounded-xl border border-amber-200 bg-amber-50 p-5">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="mt-1 h-5 w-5 flex-shrink-0 text-amber-700" />
                      <div>
                        <h3 className="font-bold text-amber-950">Point CA à retenir</h3>
                        <p className="mt-1 text-sm leading-6 text-amber-900">
                          Le mémo n’est pas seulement une vente d’actions. Le bloc principal du 1er janvier représente 16,00 % vendu à AcquisiCo/Richard via BZ direct et Octet. Le 18,00 % est une vue globale historique si on inclut le 2,00 % de Jérôme déjà transféré à Dany en avril; ce 2,00 % est conservé par Dany et ne circule pas vers AcquisiCo/Richard.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'vendeurs' && (
                <div className="space-y-5">
                  <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                    <h3 className="text-lg font-bold text-slate-950">Pont des vendeurs vérifié au mémo</h3>
                    <p className="mt-2 text-sm leading-6 text-slate-600">
                      Lecture prudente: le mémo supporte 16,00 % pour le bloc transactionnel AcquisiCo/Richard/B&D/DB du 1er janvier. Le 2,00 % de Jérôme est déjà transféré à Dany, qui le conserve; il sert seulement à expliquer la vue globale historique de 18,00 %.
                    </p>
                  </section>
                  <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                    {sellerBridge.map((item) => (
                      <section key={item.seller} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                          <h4 className="font-bold text-slate-950">{item.seller}</h4>
                          <span className="rounded-full bg-amber-100 px-3 py-1 text-sm font-bold text-amber-800">{item.sold}</span>
                        </div>
                        <p className="text-sm font-semibold text-slate-800">{item.buyer}</p>
                        <p className="mt-2 text-sm leading-6 text-slate-600">{item.note}</p>
                        <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-slate-400">{item.source}</p>
                      </section>
                    ))}
                  </div>
                </div>
              )}

              {activeTab === 'structure' && (
                <div className="space-y-4">
                  {ownership.map((holder) => <OwnershipBar key={holder.name} {...holder} />)}
                </div>
              )}

              {activeTab === 'acquisico' && (
                <div className="space-y-5">
                  <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                      <Metric label="Actionnaires" value="7" note="Selon l’actionnariat final d’AcquisiCo au 1er avril 2026." />
                      <Metric label="Actions totales" value="2 240" note="Actions votantes et participantes à dividendes discrétionnaires." />
                      <Metric label="Lien économique BZ" value="14,00 %" note="AcquisiCo détient 10,50 % direct BZ + 16,67 % d’Octet." />
                    </div>
                  </section>
                  <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                    <h3 className="mb-4 text-lg font-bold text-slate-950">Actionnariat final de 9557-1782 Québec inc.</h3>
                    <div className="space-y-4">
                      {acquisiCoShareholders.map((holder) => (
                        <div key={holder.name} className="rounded-lg border border-slate-100 bg-slate-50 p-4">
                          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                            <div>
                              <h4 className="font-bold text-slate-950">{holder.name}</h4>
                              <p className="text-sm text-slate-500">{holder.shares}</p>
                            </div>
                          </div>
                          <PercentagePill value={holder.percentage} />
                        </div>
                      ))}
                    </div>
                  </section>
                </div>
              )}

              {activeTab === 'fiscal' && (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  {fiscalBlocks.map((block) => (
                    <section key={block.title} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                      <div className="mb-3 flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-50 text-emerald-700">
                          <CheckCircle2 className="h-5 w-5" />
                        </div>
                        <div>
                          <h3 className="font-bold text-slate-950">{block.title}</h3>
                          <p className="text-sm font-semibold text-emerald-700">{block.value}</p>
                        </div>
                      </div>
                      <p className="text-sm leading-6 text-slate-600">{block.text}</p>
                    </section>
                  ))}
                </div>
              )}

              {activeTab === 'calendrier' && (
                <div className="space-y-4">
                  {timeline.map((item) => (
                    <div key={item.date} className="grid grid-cols-1 gap-3 rounded-lg border border-slate-200 bg-white p-4 shadow-sm md:grid-cols-[160px_1fr]">
                      <div className="font-bold text-amber-700">{item.date}</div>
                      <div>
                        <h3 className="font-bold text-slate-950">{item.title}</h3>
                        <p className="mt-1 text-sm leading-6 text-slate-600">{item.text}</p>
                      </div>
                    </div>
                  ))}
                  <section className="rounded-xl border border-red-200 bg-red-50 p-5">
                    <h3 className="font-bold text-red-950">Risques à surveiller</h3>
                    <p className="mt-2 text-sm leading-6 text-red-900">
                      Qualification AAPE, documentation légale signée, résolutions FiducieFZ, feuillets fiscaux 2027, IMR potentiel et validation du traitement fiscal américain de Dany.
                    </p>
                  </section>
                </div>
              )}

              {activeTab === 'sources' && (
                <div className="grid grid-cols-1 gap-5 lg:grid-cols-[0.9fr_1.1fr]">
                  <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                    <h3 className="mb-4 text-lg font-bold text-slate-950">Documents sources</h3>
                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-950">
                      Les fichiers sources ne sont pas exposés dans le site public. Ils demeurent conservés hors site public dans les emplacements internes autorisés.
                    </div>
                    <div className="mt-5 space-y-3 text-sm leading-6 text-slate-600">
                      <p><strong>PDF:</strong> 2026-07-03 - Mémo BZ_Ventes au 1er janvier 2026 - Version sans Yannick.pdf</p>
                      <p><strong>Courriel:</strong> transfert Gmail du 6 juillet 2026 contenant le message original de Maxime du 3 juillet 2026.</p>
                    </div>
                  </section>

                  <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                    <div className="mb-4 flex items-center gap-3">
                      <Building2 className="h-5 w-5 text-slate-700" />
                      <h3 className="text-lg font-bold text-slate-950">Chaîne de source</h3>
                    </div>
                    <div className="space-y-3 text-sm leading-6 text-slate-700">
                      <p><strong>Maxime Lamoureux, DFK</strong> envoie la version du 3 juillet 2026.</p>
                      <p><strong>Dany Bourassa</strong> transfère le mémo le 6 juillet 2026 avec la note: “Voici le memo a jour.”</p>
                      <p><strong>PRIsME CA</strong> conserve la synthèse dans la modale; les pièces sources sont conservées hors site public.</p>
                    </div>
                  </section>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
